import { useEffect, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { useQuery } from '@tanstack/react-query'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import { Building2, ChevronRight, Search, Store, UserCircle2 } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent } from '@/components/ui/card'
import { Spinner } from '@/components/ui/spinner'
import { AuthTermsDisclosure } from '@/components/auth/auth-terms-disclosure'
import { BackendServerSelector } from '@/components/auth/backend-server-selector'
import { useSelectedBackendServerUrl } from '@/lib/backend-servers'
import { orgTreeApi } from '@/services/org-tree'
import { getAuthThemeServiceInstanceId, useAuthTheme } from '@/lib/auth-theme'
import { resolveServiceInstanceAuthLogos } from '@/lib/auth-branding'
import { TenantAuthShell } from '@/components/auth/tenant-auth-shell'
import { isRootOrgServiceInstance } from '@/lib/org-hierarchy-from-instance'

const lookupSchema = z.object({
  reference: z.string().trim().min(1, 'Organization ID or Service ID or Identifier is required'),
})

type LookupFormData = z.infer<typeof lookupSchema>

export default function SignupVendorLookupPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const selectedBackendServerUrl = useSelectedBackendServerUrl()
  const authTheme = useAuthTheme()
  const [isLoading, setIsLoading] = useState(false)
  const searchParams = new URLSearchParams(location.search)
  const urlReference = searchParams.get('org')
    || searchParams.get('id')
    || searchParams.get('tenant')
    || searchParams.get('company_identifier')
    || searchParams.get('service_instance_id')
    || ''
  const themeReference = authTheme.locked ? getAuthThemeServiceInstanceId(authTheme) : ''
  const queryReference = themeReference || urlReference

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LookupFormData>({
    resolver: zodResolver(lookupSchema),
    defaultValues: {
      reference: queryReference,
    },
  })

  const organizationQuery = useQuery({
    queryKey: ['signup-vendor-lookup-service-instance', selectedBackendServerUrl, queryReference],
    queryFn: () => orgTreeApi.resolvePublicSignupServiceInstance(queryReference),
    enabled: queryReference.length >= 3,
    staleTime: 60_000,
    retry: false,
    placeholderData: (previousData) => previousData,
  })

  const organization = (authTheme.locked ? authTheme.organization : null) ?? organizationQuery.data
  const organizationName = organization?.name
    || organization?.title
    || organization?.identifier
    || organization?.url_identifier
    || 'Organization'
  const { sidePanelLogos, welcomeLogos } = resolveServiceInstanceAuthLogos(organization)
  const termsText = organization?.terms_and_conditions ?? null
  const privacyText = organization?.privacy_policy ?? null
  const primaryColor = organization?.primary_color || authTheme.primaryColor
  const canShowOrganizationSignup = isRootOrgServiceInstance(organization) || (!authTheme.locked && !queryReference)

  useEffect(() => {
    if (!authTheme.locked || !themeReference) {
      return
    }

    navigate(`/signup/vendor/${encodeURIComponent(themeReference)}`, { replace: true })
  }, [authTheme.locked, navigate, themeReference])

  const onSubmit = async ({ reference }: LookupFormData) => {
    setIsLoading(true)
    try {
      void selectedBackendServerUrl
      const instance = await orgTreeApi.resolvePublicSignupServiceInstance(reference)
      if (!instance) {
        toast.error('No active organization was found for that Organization ID or Service ID or Identifier.')
        return
      }

      const resolvedId = instance.instance_id || String(instance.id)
      navigate(`/signup/vendor/${encodeURIComponent(resolvedId)}`)
    } catch {
      toast.error('Unable to look up the organization right now. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <TenantAuthShell
      organizationName={organizationName}
      sidePanelLogos={sidePanelLogos}
      welcomeLogos={welcomeLogos}
      primaryColor={primaryColor}
      icon={Store}
      rightLogoContainerClassName="relative z-10 mb-4 mt-0 flex w-full flex-col items-center justify-center text-center"
      eyebrow=""
      title=""
      description=""
      panelTitle=""
      panelDescription=""
      logoPosition="left"
      contentVerticalAlign="center"
      contentPanelClassName="rounded-[24px] bg-white/95 px-5 py-5 text-foreground shadow-xl shadow-slate-950/15 backdrop-blur sm:px-6 sm:py-6"
      sideActions={(
        <div className="space-y-3 text-sm">
          <p className="text-primary-foreground/80">Other flows</p>
          <div className="flex flex-wrap justify-center gap-2">
            <Link to="/signup" className="inline-flex items-center gap-2 rounded-full bg-white/95 px-4 py-2 font-semibold text-primary shadow-sm transition hover:bg-white">
              <UserCircle2 className="h-4 w-4" />
              User
            </Link>
            {canShowOrganizationSignup ? (
              <Link to="/signup/company" className="inline-flex items-center gap-2 rounded-full bg-white/15 px-4 py-2 font-semibold text-white ring-1 ring-white/30 transition hover:bg-white/25">
                <Building2 className="h-4 w-4" />
                Organization
              </Link>
            ) : null}
          </div>
        </div>
      )}
    >
      <Card className="border-0 bg-transparent shadow-none">
        <CardContent className="px-0">
          <BackendServerSelector disabled={isLoading} className="mx-auto mb-4 max-w-md" />
          <form onSubmit={handleSubmit(onSubmit)} className="mx-auto max-w-md space-y-3">
            <div className="space-y-2">
              <Label htmlFor="reference" className="sr-only">Organization ID or Service ID or Identifier</Label>
              <Input
                id="reference"
                className="h-11 rounded-full border-0 bg-primary/15 px-5 text-center shadow-none placeholder:text-primary/45 focus-visible:ring-primary/35"
                placeholder="Organization ID or Identifier"
                autoComplete="off"
                spellCheck={false}
                disabled={isLoading}
                {...register('reference')}
                aria-invalid={!!errors.reference}
              />
              {errors.reference ? <p className="text-center text-sm text-destructive">{errors.reference.message}</p> : null}
            </div>

            <Button type="submit" className="mx-auto flex h-11 min-w-44 rounded-full px-8" disabled={isLoading}>
              {isLoading ? <Spinner className="mr-2" /> : <Search className="mr-2 h-4 w-4" />}
              Continue
            </Button>
          </form>
          <AuthTermsDisclosure
            termsText={termsText}
            privacyText={privacyText}
            className="mt-5 text-center"
          />

          <div className="mt-6 text-center text-sm text-muted-foreground">
            Already have an account?{' '}
            <Link to="/login" className="font-semibold text-primary hover:underline">
              Sign in
            </Link>
            <div className="mt-3 flex items-center justify-center gap-3 text-xs">
              <Link to="/signup" className="inline-flex items-center gap-1 font-medium text-muted-foreground hover:text-primary">
                Member <ChevronRight className="h-3 w-3" />
              </Link>
              {canShowOrganizationSignup ? (
                <Link to="/signup/company" className="inline-flex items-center gap-1 font-medium text-muted-foreground hover:text-primary">
                  Organization <ChevronRight className="h-3 w-3" />
                </Link>
              ) : null}
            </div>
          </div>
        </CardContent>
      </Card>
    </TenantAuthShell>
  )
}
